import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import type { ECharts, EChartsCoreOption } from "echarts/core";
import {
  useSkuDetailV1SkusPlatformProductIdGet,
  useSkuStoresV1SkusPlatformProductIdStoresGet,
  useSkuInventoryV1SkusPlatformProductIdInventoryGet,
  useSkuPdpV1SkusPlatformProductIdPdpGet,
  useSkuStoreMapV1SkusPlatformProductIdStoreMapGet,
} from "../api/generated/qcsControlPlane";
import { useBreadcrumbTail } from "../components/breadcrumbs";
import { EChart, cssVar } from "../components/EChart";
import { SkuSalesChart } from "../components/SkuSalesChart";
import { ChartNotes, TOD_BUCKETS, todColors, money } from "../components/sku-chart-display";
import {
  Badge,
  Button,
  type DotTone as Tone,
  ErrorState,
  PageHeader,
  Panel,
  Skeleton,
  SkeletonRegion,
  Stack,
} from "@akaike/ui";
import { toneVariant } from "../lib/badge-tone";
import { Seg, NativeSelect as Select, Stat } from "../components/foundation";
import { type Theme, useTheme } from "../lib/theme-store";
import { AnimatedNumber } from "../lib/motion";
import {
  IconCollapseCorners,
  IconExpandCorners,
  IconExternal,
} from "../components/icons";
import { SkuStoreMap, createMapSync, type MapSync } from "../components/SkuStoreMap";
import { WithRoutePlatform } from "../components/UnknownPlatform";
import { TaxonomyPanel } from "../components/TaxonomyPanel";
import { type AnyColumnDef, DataTable, createColumnHelper } from "../components/DataTable";
import type {
  SkuEventNoPid,
  SkuInventoryPoint,
  SkuInventorySeries,
  SkuPricePoint,
  SkuStoreRow,
  StoreState,
} from "../api/generated/model";
import {
  type Platform,
  ago,
  fmtDateTime,
  fmtInt,
  parseTs,
  pdpUrl,
  platformLabel,
} from "../api/labels";

// Human label for a raw PDP-extracted field key (snake/camel -> "Sentence case").
const PDP_LABELS: Record<string, string> = {
  name: "Name",
  brand: "Brand",
  manufacturer: "Manufacturer",
  pack_size: "Pack size",
  product_type: "Product type",
  variant_id: "Variant ID",
  sku: "SKU",
  mrp: "MRP",
  selling_price: "Selling price",
  is_out_of_stock: "Out of stock",
};
const PDP_ORDER = Object.keys(PDP_LABELS);

// Per-SKU detail page: every field the brand sweep persists for one product,
// the dimension row, a price/availability trend across runs, the latest sweep's
// per-store snapshot, and its lifecycle timeline. Reached from the Brands page
// (SKU name -> here); the ↗ icon there still opens the live storefront PDP.
export function SkuDetail() {
  const { platform = "", productId = "" } = useParams<{
    platform: string;
    productId: string;
  }>();
  return (
    <WithRoutePlatform platform={platform}>
      {(key) => <SkuDetailView platform={key} productId={productId} />}
    </WithRoutePlatform>
  );
}

function SkuDetailView({ platform, productId }: { platform: Platform; productId: string }) {
  const [params] = useSearchParams();
  const from = params.get("from"); // optional originating brand id, for the breadcrumb
  const skuStoreHref = useCallback(
    (storeId: string) =>
      `/skus/${platform}/${encodeURIComponent(productId)}/stores/${encodeURIComponent(storeId)}${from ? `?from=${from}` : ""}`,
    [platform, productId, from],
  );
  // one bus shared by the two SKU maps so pan/zoom on either drives the other
  // (lazy-init so the factory runs once, not on every render)
  const mapSyncRef = useRef<MapSync | null>(null);
  const mapSync = (mapSyncRef.current ??= createMapSync());

  // Per-store snapshot columns: numeric price/stock columns sort on the raw
  // number (formatted in `cell`); the stock pill doubles as a faceted filter.
  const storeColumns = useMemo<AnyColumnDef<SkuStoreRow>[]>(() => {
    const col = createColumnHelper<SkuStoreRow>();
    return [
      col.accessor((r) => r.name || r.store_id, {
        id: "store",
        header: "Store",
        cell: ({ row }) => (
          <Link to={skuStoreHref(row.original.store_id)} title="Open this SKU at this store">
            <span className="strong" style={{ fontSize: 11.5 }}>{row.original.name || row.original.store_id}</span>
            {row.original.name && (
              <div className="mono faint" style={{ fontSize: 10 }}>{row.original.store_id}</div>
            )}
          </Link>
        ),
        meta: {
          minWidth: 200,
          exportValue: (r) => r.name || r.store_id,
          searchValue: (r) => `${r.name ?? ""} ${r.store_id}`,
        },
      }),
      col.accessor((r) => [r.locality, r.city].filter(Boolean).join(", "), {
        id: "location",
        header: "Location",
        cell: ({ row }) => (
          <span className="faint" style={{ fontSize: 11.5 }}>
            {[row.original.locality, row.original.city].filter(Boolean).join(", ") || "—"}
          </span>
        ),
        meta: { minWidth: 160 },
      }),
      col.display({
        id: "tier",
        header: "Tier",
        cell: ({ row }) => (
          <span className="faint" style={{ fontSize: 11.5 }}>{row.original.tier || "\u2014"}</span>
        ),
        meta: { minWidth: 120 },
        enableSorting: false,
      }),
      col.accessor((r) => r.selling_price, {
        id: "selling",
        header: "Selling",
        cell: ({ row }) => money(row.original.selling_price),
        meta: {
          align: "right",
          cellClassName: "num",
          width: 100,
          exportValue: (r) => r.selling_price ?? "",
        },
      }),
      col.accessor((r) => r.mrp, {
        id: "mrp",
        header: "MRP",
        cell: ({ row }) => money(row.original.mrp),
        meta: {
          align: "right",
          cellClassName: "num faint",
          width: 100,
          exportValue: (r) => r.mrp ?? "",
        },
      }),
      col.accessor((r) => STORE_STATE_LABEL[r.store_state], {
        id: "state",
        header: "State",
        cell: ({ row }) => (
          <Badge variant={toneVariant(STORE_STATE_TONE[row.original.store_state])} style={{ fontSize: 10 }}>
            {STORE_STATE_LABEL[row.original.store_state]}
          </Badge>
        ),
        meta: {
          align: "right",
          width: 130,
          filterSelect: true,
          filterLabel: "State",
        },
      }),
      col.accessor((r) => r.fetched_at, {
        id: "seen",
        header: "Seen",
        cell: ({ row }) => <span className="faint" style={{ fontSize: 11 }}>{ago(row.original.fetched_at)}</span>,
        meta: { align: "right", width: 120, exportValue: (r) => r.fetched_at },
      }),
    ];
  }, [skuStoreHref]);

  // Lifecycle events: filter by event type, sort by time; transition + run are
  // rendered but not sortable (they read as one-off references, not orderings).
  const eventColumns = useMemo<AnyColumnDef<SkuEventNoPid>[]>(() => {
    const col = createColumnHelper<SkuEventNoPid>();
    return [
      col.accessor((r) => r.event_type, {
        id: "event",
        header: "Event",
        cell: ({ row }) => (
          <Badge variant={toneVariant(eventTone(row.original.event_type))} style={{ fontSize: 10.5 }}>
            {row.original.event_type}
          </Badge>
        ),
        meta: { width: 140, filterSelect: true, filterLabel: "Event" },
      }),
      col.accessor((r) => `${r.prev_status || "∅"} → ${r.new_status || "∅"}`, {
        id: "transition",
        header: "Transition",
        cell: ({ row }) => (
          <span className="faint" style={{ fontSize: 11 }}>
            {row.original.prev_status || "∅"} → {row.original.new_status || "∅"}
          </span>
        ),
        meta: { minWidth: 150 },
        enableSorting: false,
      }),
      col.accessor((r) => r.event_at, {
        id: "when",
        header: "When",
        cell: ({ row }) => <span className="faint">{fmtDateTime(row.original.event_at)}</span>,
        meta: { minWidth: 180, exportValue: (r) => r.event_at },
      }),
      col.accessor((r) => r.run_id, {
        id: "run",
        header: "Run",
        cell: ({ row }) => runLink(row.original.run_id),
        meta: { width: 110 },
        enableSorting: false,
      }),
    ];
  }, []);

  // Which cart run the availability map is rewound to ("" = the default all-time
  // carriage view). Scoped to that map only: the KPIs, the price chart and the store
  // table stay on latest, so the panel head has to say which run it is showing.
  const [runSel, setRunSel] = useState("");
  // Shared window for the inventory + sales panels (one request feeds both).
  const [days, setDays] = useState<"7" | "14" | "30">("14");
  // Fullscreen expands the PAIR of maps, so the state lives here (the parent that owns
  // both maps and the shared pan/zoom bus), not inside SkuStoreMap.
  const [mapsFull, setMapsFull] = useState(false);
  const [vh, setVh] = useState(() => (typeof window === "undefined" ? 900 : window.innerHeight));
  useEffect(() => {
    if (!mapsFull) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMapsFull(false);
    };
    const onResize = () => setVh(window.innerHeight);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    setVh(window.innerHeight);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [mapsFull]);
  const mapHeight = mapsFull ? Math.max(260, vh - 190) : 340;

  const q = useSkuDetailV1SkusPlatformProductIdGet(platform, encodeURIComponent(productId), {
    query: { retry: false },
  });
  // Coverage is an all-time carriage question, so it never takes the run selector.
  const mapQ = useSkuStoreMapV1SkusPlatformProductIdStoreMapGet(
    platform,
    encodeURIComponent(productId),
    undefined,
    { query: { retry: false, staleTime: 60_000 } },
  );
  // Availability at the selected run. With no selection this is the same query key as
  // above, so it is served from the same cache entry (no second request).
  const availQ = useSkuStoreMapV1SkusPlatformProductIdStoreMapGet(
    platform,
    encodeURIComponent(productId),
    runSel ? { run_id: runSel } : undefined,
    { query: { retry: false, staleTime: 60_000 } },
  );
  const invQ = useSkuInventoryV1SkusPlatformProductIdInventoryGet(
    platform,
    encodeURIComponent(productId),
    { days: Number(days) },
    { query: { retry: false, staleTime: 60_000 } },
  );
  const pdpQ = useSkuPdpV1SkusPlatformProductIdPdpGet(platform, encodeURIComponent(productId), {
    query: { retry: false },
  });
  // The store universe is the biggest read on the page, so it loads beside the header
  // rather than inside it -- the dimension row paints without waiting on 2,600 stores.
  const storesQ = useSkuStoresV1SkusPlatformProductIdStoresGet(
    platform,
    encodeURIComponent(productId),
    { query: { retry: false, staleTime: 60_000 } },
  );

  const s = q.data;
  const storeRows = useMemo(() => storesQ.data ?? [], [storesQ.data]);
  // The three numbers the panel header carries: how many stores we could have found this
  // SKU in, how many actually list it, and how many of those we have a recent price for.
  const storeCounts = useMemo(() => {
    const carried = storeRows.filter((r) => r.store_state !== "not_carried" && r.store_state !== "never_checked");
    return {
      total: storeRows.length,
      carried: carried.length,
      priced: storeRows.filter((r) => r.selling_price != null).length,
    };
  }, [storeRows]);
  const mapStores = mapQ.data?.stores ?? [];
  const availStores = availQ.data?.stores ?? [];
  const pdp = pdpQ.data;
  useBreadcrumbTail(
    from && s
      ? [{ label: s.brand || "brand", to: `/brands/${from}` }, { label: s.name || productId }]
      : [{ label: s?.name || productId }],
  );

  const head = (
    <PageHeader
      className="mb-3.5"
      title={s?.name || productId}
      titleMeta={
        <>
          <span className={`plat ${platform}`} style={{ fontSize: 13 }}>{platformLabel(platform)}</span>
          {s && (
            <Badge variant={toneVariant(statusTone(s.status))}>{s.status || "—"}</Badge>
          )}
          {s && (
            <a
              href={pdpUrl(platform, s.product_id)}
              target="_blank"
              rel="noopener noreferrer"
              className="as-link"
              title="Open product page on the storefront"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontSize: 12.5,
              }}
            >
              <span style={{ fontSize: 12, display: "inline-flex" }}><IconExternal /></span>
              Storefront
            </a>
          )}
        </>
      }
      description={
        <>
          {platformLabel(platform)} brand-sweep SKU
          {s?.brand ? ` · ${s.brand}` : ""}
          {s?.pack_size ? ` · ${s.pack_size}` : ""}
        </>
      }
    />
  );

  if (q.isError) {
    return (
      <>
        {head}
        <Panel>
          <Panel.Body>
            <ErrorState
              compact
              title="Could not load this SKU"
              message={
                <>
                  SKU <span className="mono">{productId}</span> may not be in the current{" "}
                  {platformLabel(platform)} dimension (never discovered, or wiped).
                </>
              }
              onRetry={() => q.refetch()}
            />
          </Panel.Body>
        </Panel>
      </>
    );
  }
  if (!s) {
    return (
      <>
        {head}
        <Panel>
          <Panel.Body tight>
            <SkeletonRegion>
              <Stack gap="sm" className="p-4">
                {Array.from({ length: 3 }, (_, i) => (
                  <Skeleton key={i} h={28} />
                ))}
              </Stack>
            </SkeletonRegion>
          </Panel.Body>
        </Panel>
      </>
    );
  }

  const latest = s.price_history[0]; // newest run first
  const sell = priceRange(latest?.sell_min, latest?.sell_max);
  const mrp = priceRange(latest?.mrp_min, latest?.mrp_max);
  const disc = discountPct(latest?.sell_min, latest?.mrp_max);
  const avail = latest
    ? `${fmtInt(latest.in_stock)} / ${fmtInt(latest.stores)}`
    : "—";

  const mapCounts = {
    carried: mapStores.filter((m) => m.found).length,
    listed_now: availStores.filter((m) => m.in_latest).length,
    in_stock: availStores.filter((m) => m.in_latest && m.is_out_of_stock === false).length,
  };
  const availAt = availQ.data?.fetched_at || "";

  return (
    <>
      {head}

      {/* at-a-glance KPIs from the most recent sweep */}
      <div className="grid g-4" style={{ marginBottom: 14 }}>
        <Stat surface label="selling price" value={sell} />
        <Stat surface label="MRP" value={mrp} />
        <Stat surface label="discount" value={disc == null ? "—" : <AnimatedNumber value={disc} format={(n) => `${Math.round(n)}% off`} />} tone={disc ? "ok" : undefined} />
        <Stat
          surface
          label="in stock (stores)"
          value={avail}
          tone={latest && latest.in_stock === 0 ? "warn" : undefined}
        />
      </div>

      {/* The merged Product panel is tall, so the three time-series stack BESIDE it in
          one column rather than leaving half a screen of white space next to it. */}
      <div
        className="grid"
        style={{
          gridTemplateColumns: "repeat(12,1fr)",
          marginBottom: 14,
          alignItems: "start",
        }}
        >
        <div className="col" style={{ gridColumn: "span 7", gap: 14, minWidth: 0 }}>
        {/* price/availability trend */}
        <Panel>
          <Panel.Head title="Price & availability over time">
            <div className="flex-1" />
            <span className="faint" style={{ fontSize: 11 }}>
              last {s.price_history.length} sweeps{sweepSpan(s.price_history)}
            </span>
          </Panel.Head>
          <Panel.Body tight>
            <PriceTrend points={s.price_history} />
            {s.price_history.length === 0 && (
              <div className="muted" style={{ padding: 16, textAlign: "center" }}>
                No price observations yet: run a brand sweep to populate history.
              </div>
            )}
          </Panel.Body>
        </Panel>

        {/* inventory level + estimated sales, both from ONE request so they can never
            show different windows */}
        <Panel>
          <Panel.Head title="Inventory over time">
            <div className="flex-1" />
            <Seg
              value={days}
              options={[
                { value: "7", label: "7d" },
                { value: "14", label: "14d" },
                { value: "30", label: "30d" },
              ]}
              onChange={(v) => setDays(v)}
            />
          </Panel.Head>
          <Panel.Body tight>
            <InventoryChart series={invQ.data} loading={invQ.isLoading} />
          </Panel.Body>
        </Panel>

        <Panel>
          <Panel.Head title="Sales over time (estimated)">
            <div className="flex-1" />
            <span className="faint" style={{ fontSize: 11 }}>last {days} days</span>
          </Panel.Head>
          <Panel.Body tight>
            <SkuSalesChart series={invQ.data} loading={invQ.isLoading} />
          </Panel.Body>
        </Panel>
        </div>

        {/* one panel for the whole product identity: what used to be four (Details,
            PDP details, Provenance, Taxonomy) as labelled sub-sections */}
        <Panel style={{ gridColumn: "span 5" }}>
          <Panel.Head title="Product" />
          <Panel.Body tight>
            <a
              href={pdpUrl(platform, s.product_id)}
              target="_blank"
              rel="noopener noreferrer"
              title="Open product page on the storefront"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 168,
                margin: "2px 0 12px",
                borderRadius: 8,
                border: "1px solid var(--line)",
                background: "var(--img-plate)",
                overflow: "hidden",
              }}
            >
              {s.image_url ? (
                <img
                  src={s.image_url}
                  alt={s.name}
                  loading="lazy"
                  style={{
                    maxHeight: 160,
                    maxWidth: "92%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <span className="faint" style={{ fontSize: 12 }}>no image captured</span>
              )}
            </a>

            <div className="panel-sub">Details</div>
            <table className="tbl">
              <tbody>
                <Row k="Product ID" v={<span className="mono">{s.product_id}</span>} />
                <Row k="Variant ID" v={s.variant_id ? <span className="mono">{s.variant_id}</span> : dash} />
                <Row k="SKU" v={s.sku ? <span className="mono">{s.sku}</span> : dash} />
                <Row k="Platform" v={platformLabel(s.platform)} />
                <Row k="Brand" v={s.brand || dash} />
                <Row k="Manufacturer" v={s.manufacturer || dash} />
                <Row k="Pack size" v={s.pack_size || dash} />
                <Row k="Category" v={(s.category_path ?? []).join(" \u203a ") || dash} />
                <Row k="Product type" v={s.product_type || dash} />
                <Row k="Status" v={<Badge variant={toneVariant(statusTone(s.status))} style={{ fontSize: 10.5 }}>{s.status || "\u2014"}</Badge>} />
              </tbody>
            </table>

            <div className="panel-sub">PDP</div>
            {pdpQ.isLoading && <div className="muted" style={{ padding: 8 }}>Loading PDP…</div>}
            {!pdpQ.isLoading && !pdp?.captured && (
              <div className="muted" style={{ padding: 10 }}>
                No PDP capture for this SKU: it was discovered via search only, or its cached PDP
                (30-day retention) has expired. The next brand sweep re-captures it.
              </div>
            )}
            {!pdpQ.isLoading && pdp?.captured && (
              <>
                <PdpFields fields={pdp.fields ?? {}} />
                {pdp.fetched_at && (
                  <div className="faint" style={{ fontSize: 11, marginTop: 6 }}>
                    captured {ago(pdp.fetched_at)}
                    {pdp.http_status ? ` \u00b7 HTTP ${pdp.http_status}` : ""}
                  </div>
                )}
              </>
            )}

            <div className="panel-sub">Provenance</div>
            <table className="tbl">
              <tbody>
                <Row k="First seen" v={`${fmtDateTime(s.first_seen_at)} \u00b7 ${ago(s.first_seen_at)}`} />
                <Row k="Last seen" v={`${fmtDateTime(s.last_seen_at)} \u00b7 ${ago(s.last_seen_at)}`} />
                <Row k="Updated" v={fmtDateTime(s.updated_at)} />
                <Row k="First seen run" v={runLink(s.first_seen_run_id)} />
                <Row k="Last seen run" v={runLink(s.last_seen_run_id)} />
              </tbody>
            </table>

            {/* the one sub-section that WRITES (classify / edit) -- kept visually grouped
                so it does not read as part of the read-only field list above */}
            <div className="panel-sub">Taxonomy</div>
            <TaxonomyPanel platform={platform} productId={productId} embedded />
          </Panel.Body>
        </Panel>
      </div>

      {/* two SKU maps: coverage (all-time carriage) and availability (one run).
          Fullscreen expands the PAIR side by side, still pan/zoom synced. */}
      <div
        className={mapsFull ? "map--full map--full__split sku-mappair" : "grid sku-mappair"}
        style={
          mapsFull
            ? undefined
            : {
                gridTemplateColumns: "repeat(12,1fr)",
                marginBottom: 14,
                alignItems: "stretch",
              }
        }
      >
        <Panel style={mapsFull ? undefined : { gridColumn: "span 6" }}>
          <Panel.Head title="Coverage">
            <div className="flex-1" />
            <span className="faint" style={{ fontSize: 11 }}>
              {mapCounts.carried} carried / {fmtInt(mapStores.length)} swept
            </span>
            <FullscreenButton on={mapsFull} onToggle={() => setMapsFull((v) => !v)} />
          </Panel.Head>
          <Panel.Body tight>
            {mapQ.isLoading ? (
              <div className="muted" style={{ padding: 24, textAlign: "center" }}>Loading map…</div>
            ) : (
              <SkuStoreMap
                platform={platform}
                stores={mapStores}
                mode="coverage"
                storeHref={skuStoreHref}
                sync={mapSync}
                height={mapHeight}
              />
            )}
            {!mapsFull && (
              <span className="hint" style={{ marginTop: 6 }}>
                Every swept dark store on {platformLabel(platform)}: green where this SKU is (or was)
                carried, grey where it was searched but never found. This is an all-time question,
                so it does not follow the run selector.
              </span>
            )}
          </Panel.Body>
        </Panel>

        <Panel style={mapsFull ? undefined : { gridColumn: "span 6" }}>
          <Panel.Head title={runSel ? "Availability" : "Current availability"}>
            <div className="flex-1" />
            <Select
              value={runSel}
              onChange={(e) => setRunSel(e.currentTarget.value)}
              title="Rewind this map to a past cart sweep"
              style={{ fontSize: 11, maxWidth: 210 }}
            >
              <option value="">Latest (daily carriage)</option>
              {s.price_history.map((p) => (
                <option key={p.run_id} value={p.run_id}>
                  {fmtDateTime(p.fetched_at)} · {p.in_stock}/{p.stores} in stock
                </option>
              ))}
            </Select>
            <span className="faint" style={{ fontSize: 11 }}>
              {mapCounts.in_stock} in stock / {mapCounts.listed_now}{" "}
              {runSel ? "observed" : "listed"}
            </span>
            {availAt && (
              <Badge
                variant={runSel ? "outline-warning" : "outline"}
                className="text-[10px]"
                title={
                  runSel
                    ? "This map is not on the latest sweep"
                    : "Newest carriage observation behind this map"
                }
              >
                as of {fmtDateTime(availAt)}
              </Badge>
            )}
            <FullscreenButton on={mapsFull} onToggle={() => setMapsFull((v) => !v)} />
          </Panel.Head>
          <Panel.Body tight>
            {availQ.isLoading ? (
              <div className="muted" style={{ padding: 24, textAlign: "center" }}>Loading map…</div>
            ) : (
              <SkuStoreMap
                platform={platform}
                stores={availStores}
                mode="availability"
                storeHref={skuStoreHref}
                sync={mapSync}
                height={mapHeight}
              />
            )}
            {!mapsFull && (
              <span className="hint" style={{ marginTop: 6 }}>
                {runSel
                  ? "Where this SKU was observed in the selected sweep \u2014 green in stock, red out of stock; grey stores were not observed in that run."
                  : "Where this SKU is listed in the latest carriage sweep: green in stock, red out of stock, amber carried at an older sweep. Carriage runs daily, so it can lag the hourly cart data in the table below."}
              </span>
            )}
          </Panel.Body>
        </Panel>
      </div>

      {/* every store in the SKU's universe, not just the ones a run reached */}
      <Panel className="mb-3.5">
        <Panel.Head title="Availability by store">
          <div className="flex-1" />
          <span className="faint" style={{ fontSize: 11 }}>
            {fmtInt(storeCounts.total)} stores · {fmtInt(storeCounts.carried)} carry it ·{" "}
            {fmtInt(storeCounts.priced)} priced recently
          </span>
        </Panel.Head>
        <Panel.Body tight>
          <DataTable
            tableKey="sku-stores"
            columns={storeColumns}
            data={storeRows}
            getRowId={(st) => st.store_id}
            globalPlaceholder="Search store name / id…"
            csvFilename={`sku-${productId}-stores`}
            pinFirstColumn
            defaultHidden={["mrp"]}
            emptyMessage="No stores on this platform yet."
            isLoading={storesQ.isLoading}
          />
        </Panel.Body>
      </Panel>

      {/* lifecycle timeline */}
      <Panel>
        <Panel.Head title="Lifecycle timeline">
          <div className="flex-1" />
          <span className="num faint" style={{ fontSize: 11 }}>{s.events.length} events</span>
        </Panel.Head>
        <Panel.Body tight>
          <DataTable
            tableKey="sku-events"
            columns={eventColumns}
            data={s.events}
            getRowId={(e, i) => `${e.event_at}-${e.event_type}-${e.run_id}-${i}`}
            enableGlobalFilter={false}
            csvFilename={`sku-${productId}-events`}
            emptyMessage="No lifecycle events for this SKU yet."
            isLoading={q.isLoading}
          />
        </Panel.Body>
      </Panel>
    </>
  );
}

// -- PDP-extracted fields (exactly what the platform's PDP parser pulled) -------
function PdpFields({ fields }: { fields: Record<string, unknown> }) {
  const keys = Object.keys(fields).sort((a, b) => {
    const ia = PDP_ORDER.indexOf(a);
    const ib = PDP_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });
  if (keys.length === 0) {
    return (
      <div className="muted" style={{ padding: 16, textAlign: "center" }}>
        A PDP was captured but no structured fields could be parsed from it.
      </div>
    );
  }
  return (
    <table className="tbl">
      <tbody>
        {keys.map((k) => (
          <Row key={k} k={PDP_LABELS[k] ?? humanize(k)} v={renderPdpValue(k, fields[k])} />
        ))}
      </tbody>
    </table>
  );
}

function renderPdpValue(key: string, v: unknown): ReactNode {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (key === "mrp" || key === "selling_price") return money(Number(v));
  if (key === "variant_id" || key === "sku") return <span className="mono">{String(v)}</span>;
  return String(v);
}

function humanize(key: string): string {
  const spaced = key.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").trim();
  return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : key;
}

// -- fullscreen control (shared by both map panel heads) -----------------------
function FullscreenButton({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onToggle}
      title={on ? "Exit fullscreen (Esc)" : "Expand both maps to fullscreen"}
      aria-label={on ? "Exit fullscreen" : "Expand both maps to fullscreen"}
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 6px",
      }}
    >
      {on ? <IconCollapseCorners /> : <IconExpandCorners />}
    </Button>
  );
}

// -- shared time axis for the per-sweep charts ---------------------------------
// Sweeps are irregular, so the axis stays one slot per sweep (a real time axis would
// stretch the gaps). The labels carry the clock and the date anyway, so a window of
// 40 sweeps reads as "one busy afternoon" or "two weeks" without hovering anything.
const IST_DAY: Intl.DateTimeFormatOptions = {
  timeZone: "Asia/Kolkata",
  day: "2-digit",
  month: "short",
};
const IST_HM: Intl.DateTimeFormatOptions = {
  timeZone: "Asia/Kolkata",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

// "last 40 sweeps" says nothing about how long that took, so the header states the span
// too. Input is the API order (newest first); an empty or single-sweep history has none.
function sweepSpan(points: SkuPricePoint[]): string {
  const newest = points[0]?.fetched_at;
  const oldest = points[points.length - 1]?.fetched_at;
  if (!newest || !oldest || points.length < 2) return "";
  const hours = (parseTs(newest).getTime() - parseTs(oldest).getTime()) / 3_600_000;
  if (hours < 1) return ", over <1h";
  if (hours < 48) return `, over ${Math.round(hours)}h`;
  return `, over ${Math.round(hours / 24)} days`;
}

// Every label carries both clock and date: only a handful are drawn (see sweepAxis), and
// a time-only tick next to a blanked date-carrying one is exactly the ambiguity to avoid.
function sweepAxisLabels(times: (string | null | undefined)[]): string[] {
  return times.map((t) => {
    if (!t) return "";
    const d = parseTs(t);
    return `${d.toLocaleTimeString("en-GB", IST_HM)}\n${d.toLocaleDateString("en-GB", IST_DAY)}`;
  });
}

// Up to six labels, evenly spaced and always including the first and last sweep -- the
// two that answer "what window am I looking at". ECharts' own `interval` cannot pin both
// ends, so the ticks are picked here and the rest are blanked by the formatter.
function sweepAxis(labels: string[], faint: string, line: string, mono: string) {
  const n = labels.length;
  const ticks = new Set<number>();
  const slots = Math.min(6, n);
  for (let i = 0; i < slots; i++) ticks.add(Math.round((i * (n - 1)) / Math.max(1, slots - 1)));
  return {
    type: "category" as const,
    data: labels,
    boundaryGap: false,
    axisLine: { lineStyle: { color: line } },
    axisTick: { show: false },
    axisLabel: {
      color: faint,
      fontSize: 9,
      fontFamily: mono,
      lineHeight: 11,
      margin: 8,
      interval: 0,
      formatter: (value: string, index: number) => (ticks.has(index) ? value : ""),
    },
  };
}

// -- inventory level over time (one point per cart run) ------------------------
// Per RUN, not per day: a stock level has no meaningful daily sum, and every store in
// a run is probed in the same sweep. `qty_total` moves with the store roster as well as
// with real stock, which is why `stores` is shown and "Avg per store" is offered.
type InventoryMode = "total" | "avg";

function InventoryChart({
  series,
  loading,
}: {
  series: SkuInventorySeries | undefined;
  loading: boolean;
}) {
  const { theme } = useTheme();
  const [mode, setMode] = useState<InventoryMode>("total");
  const runs = useMemo(() => series?.runs ?? [], [series]);
  const option = useMemo<EChartsCoreOption | null>(
    () => (runs.length ? inventoryOption(runs, mode, theme) : null),
    [runs, mode, theme],
  );
  // Zoom lives in the chart, so the reset control has to read and write it imperatively.
  const chart = useRef<ECharts | null>(null);
  const [zoomed, setZoomed] = useState(false);
  useEffect(() => {
    const c = chart.current;
    if (!c) return;
    const onZoom = (e: unknown) => {
      const ev = e as {
        batch?: { start?: number; end?: number }[];
        start?: number;
        end?: number;
      };
      const { start = 0, end = 100 } = ev.batch?.[0] ?? ev;
      setZoomed(start > 0.01 || end < 99.99);
    };
    c.on("dataZoom", onZoom);
    return () => void c.off("dataZoom", onZoom);
  }, [option]);
  // a new option is applied with notMerge, which resets the window WITHOUT firing an
  // event -- so the button has to un-arm itself here or it lingers over an unzoomed chart
  useEffect(() => setZoomed(false), [option]);
  const resetZoom = () => {
    chart.current?.dispatchAction({ type: "dataZoom", start: 0, end: 100 });
    setZoomed(false);
  };
  if (loading) return <div className="muted" style={{ padding: 24, textAlign: "center" }}>Loading…</div>;
  if (!option) {
    const noQuantity = (series?.stores_without_quantity ?? 0) > 0 && (series?.stores_with_quantity ?? 0) === 0;
    return (
      <div className="muted" style={{ padding: 20, textAlign: "center" }}>
        {noQuantity
          ? "This platform does not report an inventory quantity for this SKU."
          : "No observations in this window."}
      </div>
    );
  }
  return (
    <>
      <div className="flex items-center justify-end gap-2 px-1 pb-1.5">
        <TodLegend />
        <div className="flex-1" />
        {zoomed && (
          <Button variant="outline" size="sm" onClick={resetZoom}>
            Reset zoom
          </Button>
        )}
        <Seg
          value={mode}
          options={[
            { value: "total", label: "Total" },
            { value: "avg", label: "Avg per store" },
          ]}
          onChange={(v) => setMode(v)}
        />
      </div>
      <EChart option={option} height={196} instanceRef={chart} />
      <ChartNotes
        lead="Reported quantities per sweep."
        notes={[
          <>Total sums saved quantities. Avg per store includes only stores reporting a quantity.</>,
          <>
            The total moves with the store roster too, so a jump can be new stores rather than new
            stock: “Avg per store” separates the two.
          </>,
          <>The background shades the 6h IST window each sweep landed in.</>,
          <>Drag the slider or shift + scroll to zoom; “Reset zoom” returns the full window.</>,
        ]}
      />
    </>
  );
}

// The inventory chart's bands are drawn by ECharts but named here: a wash with no key is
// just decoration, and the built-in legend would offer to toggle series that do not exist.
function TodLegend() {
  return (
    <div className="row" style={{ gap: 9, alignItems: "center" }} aria-label="IST time of day">
      {TOD_BUCKETS.map((b) => (
        <span
          key={b.label}
          className="faint row"
          // nowrap: "00-06" breaking across two lines is worse than the legend clipping
          style={{
            gap: 4,
            alignItems: "center",
            fontSize: 10,
            whiteSpace: "nowrap",
          }}
        >
          <span
            aria-hidden
            style={{
              width: 8,
              height: 8,
              borderRadius: 2,
              background: `var(${b.token}, ${b.fallback})`,
              opacity: 0.55,
            }}
          />
          {b.label}
        </span>
      ))}
    </div>
  );
}

// Which 6h IST window a timestamp falls in. The API's own bucketing is server-side IST,
// so this has to be IST too and not the viewer's clock.
// hoisted: constructing a DateTimeFormat is the expensive half of this, and a 500-sweep
// window would otherwise build one per point
const IST_HOUR = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Asia/Kolkata",
  hour: "2-digit",
  hour12: false,
});

function istBucket(ts: string): number {
  const d = new Date(ts.endsWith("Z") || ts.includes("+") ? ts : `${ts.replace(" ", "T")}Z`);
  if (Number.isNaN(d.getTime())) return 0;
  const hour = Number(IST_HOUR.format(d));
  return Math.min(3, Math.floor((hour % 24) / 6));
}

// Contiguous runs of same-bucket sweeps, as the [from, to] category pairs one markArea
// band each wants. Drawn behind the line, they turn "which part of this curve is
// overnight" into something readable without a second chart -- and they are the reason
// zooming in is worth doing at all.
function todBands(times: string[], colors: string[], opacity: number) {
  const bands: Record<string, unknown>[][] = [];
  // bucket per point once: the run-detection loop would otherwise re-read each timestamp
  // twice (once to extend a run, once as the next run's head)
  const buckets = times.map(istBucket);
  const last = buckets.length - 1;
  for (let i = 0; i < buckets.length; i++) {
    const b = buckets[i];
    let j = i;
    while (j + 1 <= last && buckets[j + 1] === b) j++;
    bands.push([
      // half a slot either side, so consecutive bands meet at the midpoint between the
      // two sweeps instead of leaving a half-slot of bare panel between them. The two
      // outer edges stay pinned to the plot edges (`boundaryGap: false`).
      {
        xAxis: i === 0 ? i : i - 0.5,
        itemStyle: { color: colors[b], opacity },
      },
      { xAxis: j === last ? j : j + 0.5 },
    ]);
    i = j;
  }
  return bands;
}

export function inventoryOption(
  points: SkuInventoryPoint[],
  mode: InventoryMode,
  theme: Theme,
): EChartsCoreOption {
  const ink2 = cssVar("--ink-2", "#444");
  const faint = cssVar("--ink-3", "#888");
  const line = cssVar("--line", "#e5e5e5");
  const brand = cssVar("--brand", "#e0a325");
  const bg = cssVar("--panel", "#fff");
  const mono = cssVar("--f-mono", "ui-monospace, monospace");
  const values = points.map((p) => {
    if (p.qty_total == null) return null;
    return mode === "avg" ? (p.stores > 0 ? p.qty_total / p.stores : null) : p.qty_total;
  });
  // Faint enough to stay behind the line: this is a background wash naming the hours, not
  // a series. Zoomed out it is texture; zoomed into a day it is the reading. Dark mode
  // needs a touch more to survive on a dark surface.
  const bands = todBands(
    points.map((p) => p.fetched_at),
    todColors(),
    theme === "control-room" ? 0.1 : 0.06,
  );
  return {
    // room at the bottom for the zoom slider
    grid: { left: 8, right: 12, top: 16, bottom: 34, containLabel: true },
    legend: { show: false },
    tooltip: {
      trigger: "axis",
      // the panel clips its overflow, so a tall tooltip near the top gets its head cut
      // off unless it is rendered on <body> instead of inside the chart container
      appendToBody: true,
      backgroundColor: bg,
      borderColor: line,
      borderWidth: 1,
      padding: [6, 9],
      textStyle: { color: ink2, fontSize: 11, fontFamily: mono },
      formatter: (raw: unknown) => {
        const arr = raw as { dataIndex: number }[];
        const p = points[arr[0]?.dataIndex];
        if (!p) return "";
        return [
          p.qty_total == null
            ? "<b>Quantity not reported</b>"
            : `<b>${fmtInt(p.qty_total)}</b> total quantity`,
          `<span style="opacity:.7">${fmtInt(p.stores)} reporting stores</span>`,
          p.qty_total != null && p.stores > 0
            ? `<span style="opacity:.7">${(p.qty_total / p.stores).toFixed(1)} avg per store</span>`
            : "",
          `<span style="opacity:.7">${fmtDateTime(p.fetched_at)}</span>`,
        ]
          .filter(Boolean)
          .join("<br/>");
      },
    },
    xAxis: sweepAxis(sweepAxisLabels(points.map((p) => p.fetched_at)), faint, line, mono),
    yAxis: {
      type: "value",
      scale: false,
      splitNumber: 3,
      axisLabel: { color: faint, fontSize: 9, fontFamily: mono },
      splitLine: { lineStyle: { color: line, type: "dashed" } },
    },
    // Drag the slider, or shift+wheel / pinch over the plot. Wheel alone stays with the
    // page: a chart that eats the scroll is worse than one that does not zoom.
    dataZoom: [
      {
        type: "inside",
        zoomOnMouseWheel: "shift",
        moveOnMouseWheel: false,
        moveOnMouseMove: true,
      },
      {
        type: "slider",
        height: 16,
        bottom: 4,
        // the x axis already names the window; the drag-time bubble only overlaps the hint
        showDetail: false,
        borderColor: line,
        backgroundColor: "transparent",
        fillerColor: cssVar("--brand-dim", "rgba(63,143,156,.11)"),
        handleStyle: { color: bg, borderColor: faint },
        moveHandleStyle: { color: line },
        dataBackground: {
          lineStyle: { color: faint, opacity: 0.5 },
          areaStyle: { opacity: 0 },
        },
        selectedDataBackground: {
          lineStyle: { color: brand },
          areaStyle: { opacity: 0 },
        },
        textStyle: { color: faint, fontSize: 9, fontFamily: mono },
      },
    ],
    series: [
      {
        name: mode === "avg" ? "Avg per store" : "Total",
        type: "line",
        data: values,
        connectNulls: false,
        showSymbol: false,
        smooth: false,
        lineStyle: { color: brand, width: 1.75 },
        itemStyle: { color: brand },
        // no area fill: the time-of-day wash is the fill now, and stacking a brand tint
        // on top of it would muddy both
        markArea: { silent: true, label: { show: false }, data: bands },
      },
    ],
  };
}

// -- price trend chart (selling price across sweeps, oldest -> newest) ----------
function PriceTrend({ points }: { points: SkuPricePoint[] }) {
  const { theme } = useTheme();
  const option = useMemo<EChartsCoreOption | null>(() => {
    if (points.length === 0) return null;
    return priceOption([...points].reverse(), theme); // API is newest-first; chart oldest-first
  }, [points, theme]);
  if (!option) return null;
  return <EChart option={option} height={170} />;
}

// Each sweep prices the SKU across many stores, so a single line is a lie: the chart
// draws the min-max and p25-p75 spread with the median on top. Bands are a transparent
// lower series plus a stacked delta carrying the areaStyle (the standard ECharts
// confidence-band recipe), so no extra chart type has to be registered.
function priceOption(points: SkuPricePoint[], _theme: Theme): EChartsCoreOption {
  const ink2 = cssVar("--ink-2", "#444");
  const faint = cssVar("--ink-3", "#888");
  const line = cssVar("--line", "#e5e5e5");
  const brand = cssVar("--brand", "#e0a325");
  const bg = cssVar("--panel", "#fff");
  const mono = cssVar("--f-mono", "ui-monospace, monospace");

  const mid = points.map((p) => p.sell_p50 ?? repPrice(p.sell_min, p.sell_max));
  const mrp = points.map((p) => repPrice(p.mrp_min, p.mrp_max));
  const labels = sweepAxisLabels(points.map((p) => p.fetched_at));
  // A band needs both edges; a sweep missing either drops out of that band only.
  const band = (
    lo: (p: SkuPricePoint) => number | null | undefined,
    hi: (p: SkuPricePoint) => number | null | undefined,
  ) => ({
    base: points.map((p) => (lo(p) != null && hi(p) != null ? (lo(p) as number) : null)),
    delta: points.map((p) => {
      const a = lo(p);
      const b = hi(p);
      return a != null && b != null ? Math.max(0, b - a) : null;
    }),
  });
  const outer = band((p) => p.sell_min, (p) => p.sell_max);
  const inner = band((p) => p.sell_p25, (p) => p.sell_p75);

  const bandSeries = (
    name: string,
    data: { base: (number | null)[]; delta: (number | null)[] },
    opacity: number,
  ) => [
    {
      name: `${name} base`,
      type: "line",
      stack: name,
      data: data.base,
      showSymbol: false,
      lineStyle: { opacity: 0 },
      itemStyle: { opacity: 0 },
      silent: true,
      legendHoverLink: false,
    },
    {
      name,
      type: "line",
      stack: name,
      data: data.delta,
      showSymbol: false,
      lineStyle: { opacity: 0 },
      itemStyle: { opacity: 0 },
      areaStyle: { color: brand, opacity },
      silent: true,
      legendHoverLink: false,
    },
  ];

  return {
    grid: { left: 8, right: 12, top: 16, bottom: 6, containLabel: true },
    legend: { show: false },
    tooltip: {
      trigger: "axis",
      // the panel clips its overflow, so a tall tooltip near the top gets its head cut
      // off unless it is rendered on <body> instead of inside the chart container
      appendToBody: true,
      backgroundColor: bg,
      borderColor: line,
      borderWidth: 1,
      padding: [6, 9],
      textStyle: { color: ink2, fontSize: 11, fontFamily: mono },
      formatter: (raw: unknown) => {
        const arr = raw as { dataIndex: number }[];
        const p = points[arr[0]?.dataIndex];
        if (!p) return "";
        const spread =
          p.sell_p25 != null && p.sell_p75 != null
            ? ` <span style="opacity:.7">(${money(p.sell_p25)} – ${money(p.sell_p75)})</span>`
            : "";
        return [
          `<b>${money(p.sell_p50 ?? repPrice(p.sell_min, p.sell_max))}</b> median${spread}`,
          `<span style="opacity:.7">range ${money(p.sell_min)} – ${money(p.sell_max)}</span>`,
          `<span style="opacity:.7">MRP ${money(repPrice(p.mrp_min, p.mrp_max))}</span>`,
          `${p.in_stock}/${p.stores} stores in stock`,
          `<span style="opacity:.7">${fmtDateTime(p.fetched_at)}</span>`,
        ].join("<br/>");
      },
    },
    xAxis: sweepAxis(labels, faint, line, mono),
    yAxis: {
      type: "value",
      scale: true,
      splitNumber: 3,
      axisLabel: {
        color: faint,
        fontSize: 9,
        fontFamily: mono,
        formatter: (v: number) => `₹${v}`,
      },
      splitLine: { lineStyle: { color: line, type: "dashed" } },
    },
    series: [
      ...bandSeries("Range", outer, 0.08),
      ...bandSeries("IQR", inner, 0.2),
      {
        name: "MRP",
        type: "line",
        data: mrp,
        connectNulls: false,
        showSymbol: false,
        smooth: false,
        lineStyle: { color: faint, width: 1, type: "dashed" },
        itemStyle: { color: faint },
      },
      {
        name: "Median selling",
        type: "line",
        data: mid,
        connectNulls: false,
        showSymbol: true,
        symbolSize: 6,
        smooth: false,
        lineStyle: { color: brand, width: 1.75 },
        itemStyle: { color: brand, borderColor: bg, borderWidth: 1 },
        emphasis: { scale: 1.5 },
      },
    ],
  };
}

// -- small helpers -------------------------------------------------------------
const dash = <span className="faint">—</span>;

// A single representative price from a per-sweep min/max (prefers the offered min).
function repPrice(min: number | null, max: number | null): number | null {
  if (min != null && min > 0) return min;
  if (max != null && max > 0) return max;
  return null;
}

function priceRange(min: number | null | undefined, max: number | null | undefined): string {
  const a = money(min);
  const b = money(max);
  if (a === "—" && b === "—") return "—";
  if (a === b || b === "—") return a;
  if (a === "—") return b;
  return `${a} – ${b}`;
}

function discountPct(sell: number | null | undefined, mrp: number | null | undefined): number | null {
  if (!sell || !mrp || mrp <= 0 || sell >= mrp) return null;
  return Math.round(((mrp - sell) / mrp) * 100);
}

function runLink(id: string): ReactNode {
  if (!id) return dash;
  return <Link className="mono" to={`/runs/${id}`} style={{ fontSize: 11 }}>{id.slice(0, 8)}</Link>;
}

function Row({ k, v }: { k: string; v: ReactNode }) {
  return (
    <tr>
      <td className="faint" style={{ whiteSpace: "nowrap", width: 1, paddingRight: 18 }}>{k}</td>
      <td className="strong" style={{ fontSize: 12.5 }}>{v}</td>
    </tr>
  );
}

function statusTone(s: string): "ok" | "warn" | "idle" {
  if (s === "active") return "ok";
  if (s === "removed") return "warn";
  return "idle";
}

// Where a store stands on this SKU, as the table words it. The keys are the closed set
// the API sends, so a state added there without a label here fails the build.
const STORE_STATE_LABEL: Record<StoreState, string> = {
  in_stock: "in stock",
  out_of_stock: "OOS",
  carried_previously: "was carried",
  not_carried: "not carried",
  never_checked: "never checked",
};

// "was carried" is the same amber the availability map draws for a store whose carriage
// verdict has aged out, so a pin and its row read alike.
const STORE_STATE_TONE: Record<StoreState, Tone> = {
  in_stock: "ok",
  out_of_stock: "warn",
  carried_previously: "info",
  not_carried: "idle",
  never_checked: "idle",
};

function eventTone(t: string): "ok" | "warn" | "info" | "idle" {
  if (t === "added") return "ok";
  if (t === "reactivated") return "info";
  if (t === "removed") return "warn";
  return "idle";
}
